import { useMutation, useQuery } from '@tanstack/react-query'
import { useIamContext } from 'ClientPortal/src/context/IAmContext'
import { getSelectedCustomer } from 'ClientPortal/src/context/SelectedCustomerContext'
import { isAppraiserUser, type AppraiserUser, type CustomerUser } from '@einwert/valuation-api-client'
import { getValuationApiClient } from 'ClientPortal/src/api/valuationApiClient'
import invariant from 'tiny-invariant'
import { ReactQueryOptions, WithReactQueryOptions } from 'ComponentLibrary'
import { Operation } from 'fast-json-patch'
import { throwOnForbiddenApiError } from 'ClientPortal/src/api/react-query-config'
import { useGlobalMutationErrorHandler } from 'ClientPortal/src/api/useGlobalMutationErrorHandler'

const CUSTOMER = 'CUSTOMER'
const CUSTOMERS = 'CUSTOMERS'

const getCustomerIdForGetCustomerQuery = ({
    user,
    withCustomerIdOfCurrentUser = false,
}: {
    user: CustomerUser | AppraiserUser | undefined
    withCustomerIdOfCurrentUser?: boolean
}): string | undefined => {
    if (user === undefined) {
        return undefined
    }

    const selectedCustomerId = getSelectedCustomer()?.uuid
    if (isAppraiserUser(user)) {
        return selectedCustomerId
    }

    if (selectedCustomerId === undefined || withCustomerIdOfCurrentUser) {
        return user.customer_uuid
    }

    return selectedCustomerId
}

export function useGetCurrentCustomer({
    enabled = true,
    throwOnError = throwOnForbiddenApiError,
    withCustomerIdOfCurrentUser = false,
    ...options
}: WithReactQueryOptions<{ withCustomerIdOfCurrentUser?: boolean }> = {}) {
    const { user } = useIamContext()
    const valuationApiClient = getValuationApiClient()
    const customerId = getCustomerIdForGetCustomerQuery({ user, withCustomerIdOfCurrentUser })

    const { data, isLoading, isError } = useQuery({
        queryKey: [CUSTOMER, customerId],
        queryFn: () => valuationApiClient.customers.getCustomer({ customerId }),
        enabled: !!customerId && enabled,
        throwOnError,
        ...options,
    })
    return {
        customer: data,
        isCustomerInitialLoading: isLoading,
        isCustomerError: isError,
    }
}

export function useFetchCurrentCustomer() {
    const { user, isUserPending } = useIamContext()
    const valuationApiClient = getValuationApiClient()
    const customerId = getCustomerIdForGetCustomerQuery({ user })
    invariant(isUserPending || customerId, 'User must have a customer_uuid or select a customer_uuid')
    const handleError = useGlobalMutationErrorHandler()

    const { mutateAsync, isPending } = useMutation({
        mutationFn: () => valuationApiClient.customers.getCustomer({ customerId }),
        onError: handleError,
    })
    return { fetchCustomer: mutateAsync, isFetchingCustomer: isPending }
}

export function usePatchCustomer({ onSuccess, onError, customerId }: WithReactQueryOptions<{ customerId?: string }>) {
    const valuationApiClient = getValuationApiClient()
    const handleError = useGlobalMutationErrorHandler(onError)

    const { isPending, isError, error, mutateAsync } = useMutation({
        mutationFn: (operations: Operation[]) =>
            valuationApiClient.customers.patchCustomer({ customerId, operations, customerUuid: getSelectedCustomer()?.uuid }),
        onSuccess,
        onError: handleError,
        meta: {
            invalidates: [[CUSTOMER, customerId]],
        },
    })

    return {
        isPatchingCustomer: isPending,
        isPatchCustomerError: isError,
        patchCustomerError: error,
        patchCustomer: mutateAsync,
    }
}

export function useFetchCustomers({ enabled }: ReactQueryOptions) {
    const valuationApiClient = getValuationApiClient()

    const { data, isLoading, isError } = useQuery({
        queryFn: () => valuationApiClient.customers.getCustomers({ customerUuid: getSelectedCustomer()?.uuid }),
        queryKey: [CUSTOMERS],
        enabled,
    })
    const customers = data?.customers || []
    return {
        customers,
        isCustomersInitialLoading: isLoading,
        isGetCustomersError: isError,
    }
}
